import { Injectable, inject } from '@angular/core';

import { Observable, catchError, filter, map, mergeMap, of, switchMap } from 'rxjs';

import { CamBaseService, GraphEndpoint, HandleComplexRequest, HandleSimpleRequest } from '@camelot/server';
import { isNonNullable } from '@camelot/utils';

import { dayAvailabilityProps } from './dto/day-availability';
import { Function, FunctionCreationPayload, FunctionModifierPayload, functionProps } from './dto/function';
import { Role, roleProps } from './dto/role';
import { UserFunctionPayload } from './dto/user';
import { ADD_FUNCTION, ADD_FUNCTION_TO_USER, REMOVE_FUNCTION_FROM_USER, UPDATE_FUNCTION } from './user-mutations';
import { GET_FUNCTIONS, GET_ROLES } from './user-queries';
import { CamUsersService } from './users.service';

const graphEndpoint: GraphEndpoint = {
  clientName: 'userService',
  endpoint: 'user',
};

@Injectable({
  providedIn: 'root',
})
export class CamFunctionsService extends CamBaseService {
  public functions = new HandleSimpleRequest<Function[]>();
  public function = new HandleComplexRequest<Function | null>();
  public roles = new HandleSimpleRequest<Role[]>();

  private _usersService = inject(CamUsersService);

  constructor() {
    super();
    super.registerRoutes({ graphEndpoint: graphEndpoint });
  }

  public fetchFunction$(id: string) {
    return this.function.fetch(
      id,
      this._graphService
        .fetchPagedQueryList<Function>(
          GET_FUNCTIONS(
            `where: { id: { eq: "${id}" }}`,
            `
              ${functionProps.get('id')}
              ${functionProps.get('name')}
              ${functionProps.get('missionTypes')}
              ${functionProps.get('dayAvailabilities')} {
                ${dayAvailabilityProps.get('id')}
                ${dayAvailabilityProps.get('dayOfWeek')}
                ${dayAvailabilityProps.get('startTime')}
                ${dayAvailabilityProps.get('endTime')}
              }
              ${functionProps.get('roles')} {
                ${roleProps.get('id')}
                ${roleProps.get('name')}
              }
              
            `
          ),
          'functions',
          graphEndpoint.clientName
        )
        .pipe(
          filter(isNonNullable),
          map(data => (data.items ? data.items[0] : null))
        )
    );
  }

  public fetchFunctions$() {
    return this.functions.fetch(
      this._graphService
        .fetchPagedQueryList<Function>(
          GET_FUNCTIONS(
            '',
            `
              ${functionProps.get('id')}
              ${functionProps.get('name')}
              ${functionProps.get('missionTypes')}
              ${functionProps.get('roles')} {
                ${roleProps.get('id')}
                ${roleProps.get('name')}
              }
            `
          ),
          'functions',
          graphEndpoint.clientName
        )
        .pipe(
          filter(isNonNullable),
          map(data => data.items ?? [])
        )
    );
  }

  public fetchRoles$() {
    return this.roles.fetch(
      this._graphService
        .fetchPagedQueryList<Role>(
          GET_ROLES(
            '',
            `
              ${roleProps.get('id')}
              ${roleProps.get('name')}
            `
          ),
          'roles',
          graphEndpoint.clientName
        )
        .pipe(
          filter(isNonNullable),
          map(data => data.items ?? [])
        )
    );
  }

  public addFunction$(functionAddedPayload: FunctionCreationPayload): Observable<Function[] | null> {
    return this._graphService
      .mutateBuilder<Function>(ADD_FUNCTION(functionAddedPayload), graphEndpoint.clientName, ['functions'])
      .pipe(
        filter(isNonNullable),
        switchMap(() => this.fetchFunctions$()),
        catchError(error => {
          console.error('Error adding function:', error);
          return of(null);
        })
      );
  }

  public updateFunction$(functionModifierPayload: Partial<FunctionModifierPayload>) {
    return this._graphService
      .mutateBuilder<Function>(UPDATE_FUNCTION(functionModifierPayload), graphEndpoint.clientName, ['functions'])
      .pipe(
        filter(isNonNullable),
        mergeMap(() => this.fetchFunction$(functionModifierPayload.id ?? ''))
      );
  }

  public addFunctionToUser$(payload: UserFunctionPayload) {
    return this._graphService
      .mutateBuilder<Function>(ADD_FUNCTION_TO_USER(payload), graphEndpoint.clientName, ['userById'])
      .pipe(
        filter(isNonNullable),
        switchMap(() => this._usersService.fetchUser$(payload.userId)),
        catchError(error => {
          console.error('Error adding function:', error);
          return of(null);
        })
      );
  }

  public removeFunctionFromUser$(payload: UserFunctionPayload) {
    return this._graphService
      .mutateBuilder<Function>(REMOVE_FUNCTION_FROM_USER(payload), graphEndpoint.clientName, ['userById'])
      .pipe(
        filter(isNonNullable),
        switchMap(() => this._usersService.fetchUser$(payload.userId)),
        catchError(error => {
          console.error('Error adding function:', error);
          return of(null);
        })
      );
  }
}
